import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { useTRPC } from '@/lib/trpc'
import { normalizeSocialConnectionId } from './connection-id'
import {
  createBrowserSocialWorkspace,
  type SocialDataEventType,
  type SocialDockMode,
  type SocialWorkspaceSnapshot,
  type SocialWorkspaceStore,
} from './social-workspace-store'

type SocialWorkspaceContextValue = SocialWorkspaceSnapshot & {
  available: boolean
  setDockMode: (mode: SocialDockMode) => void
  openConnection: (connectionId: string | null) => void
  setDraft: (connectionId: string, text: string) => void
  clearDraft: (connectionId: string) => void
  clearDraftIfMatches: (
    connectionId: string,
    expectedText: string,
  ) => boolean
  publishDataEvent: (type: SocialDataEventType, connectionId?: string) => void
}

const EMPTY_SNAPSHOT: SocialWorkspaceSnapshot = {
  dockMode: 'minimized',
  activeConnectionId: null,
  drafts: {},
}

const SocialWorkspaceContext = createContext<SocialWorkspaceContextValue>({
  ...EMPTY_SNAPSHOT,
  available: false,
  setDockMode: () => {},
  openConnection: () => {},
  setDraft: () => {},
  clearDraft: () => {},
  clearDraftIfMatches: () => false,
  publishDataEvent: () => {},
})

export function isSocialQuery(query: { queryKey: readonly unknown[] }): boolean {
  return JSON.stringify(query.queryKey).includes('socialRouter')
}

function useStoreSnapshot(
  store: SocialWorkspaceStore | null,
): SocialWorkspaceSnapshot {
  return useSyncExternalStore(
    store?.subscribe ?? (() => () => {}),
    store?.getSnapshot ?? (() => EMPTY_SNAPSHOT),
    () => EMPTY_SNAPSHOT,
  )
}

export function SocialWorkspaceProvider({
  userId,
  children,
}: {
  userId: string | null
  children: ReactNode
}) {
  const queryClient = useQueryClient()
  const [storeEntry, setStoreEntry] = useState<{
    userId: string
    store: SocialWorkspaceStore
  } | null>(null)
  const store = storeEntry?.userId === userId ? storeEntry.store : null
  const snapshot = useStoreSnapshot(store)
  const previousUserId = useRef<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setStoreEntry(null)
      return
    }
    const nextStore = createBrowserSocialWorkspace(userId)
    setStoreEntry({ userId, store: nextStore })
    return () => nextStore.destroy()
  }, [userId])

  useEffect(() => {
    const oldUserId = previousUserId.current
    if (oldUserId && oldUserId !== userId) {
      try {
        localStorage.removeItem(`mentiss:social:v1:${oldUserId}`)
        localStorage.removeItem(`mentiss:social:event:v1:${oldUserId}`)
      }
      catch {}
      queryClient.removeQueries({ predicate: isSocialQuery })
    }
    previousUserId.current = userId
  }, [queryClient, userId])

  useEffect(() => {
    if (!store)
      return
    return store.subscribeToDataEvents(() => {
      void queryClient.invalidateQueries({ predicate: isSocialQuery })
    })
  }, [queryClient, store])

  useEffect(() => {
    if (!store)
      return
    const refresh = () => {
      void queryClient.invalidateQueries({ predicate: isSocialQuery })
    }
    window.addEventListener('focus', refresh)
    window.addEventListener('online', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener('online', refresh)
    }
  }, [queryClient, store])

  const value = useMemo<SocialWorkspaceContextValue>(
    () => ({
      ...snapshot,
      available: !!store,
      setDockMode: mode => store?.setDockMode(mode),
      openConnection: connectionId => store?.openConnection(connectionId),
      setDraft: (connectionId, text) => store?.setDraft(connectionId, text),
      clearDraft: connectionId => store?.clearDraft(connectionId),
      clearDraftIfMatches: (connectionId, expectedText) =>
        store?.clearDraftIfMatches(connectionId, expectedText) ?? false,
      publishDataEvent: (type, connectionId) =>
        store?.publishDataEvent(type, connectionId),
    }),
    [snapshot, store],
  )

  return (
    <SocialWorkspaceContext.Provider value={value}>
      {children}
    </SocialWorkspaceContext.Provider>
  )
}

export function useSocialWorkspace(): SocialWorkspaceContextValue {
  return useContext(SocialWorkspaceContext)
}

export function useSocialOverview(enabled = true) {
  const trpc = useTRPC()
  const { activeConnectionId } = useSocialWorkspace()
  const normalizedActiveConnectionId
    = normalizeSocialConnectionId(activeConnectionId)
  return useQuery({
    ...trpc.socialRouter.getOverview.queryOptions({
      activeConnectionId: normalizedActiveConnectionId ?? undefined,
    }),
    enabled,
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
        ? 45_000
        : 12_000,
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
}

export function useInvalidateSocial() {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ predicate: isSocialQuery }),
    [queryClient],
  )
}
